import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SuperAdminRoute } from "@/components/SuperAdminRoute";
import { SuperAdminBanner } from "@/components/SuperAdminBanner";
import { SoftCloseBanner } from "@/components/SoftCloseBanner";
import SuperAdminDashboard from "./pages/superadmin/SuperAdminDashboard";
import SuperAdminClinicDetail from "./pages/superadmin/SuperAdminClinicDetail";
import Index from "./pages/Index";
import LandingPage from "./pages/LandingPage";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NoAccess from "./pages/NoAccess";
import NotFound from "./pages/NotFound";
import Policies from "./pages/Policies";
import PolicyDetail from "./pages/PolicyDetail";
import PendingAcknowledgements from "./pages/PendingAcknowledgements";
import AuditExport from "./pages/AuditExport";
import CreatePolicy from "./pages/CreatePolicy";
import Settings from "./pages/Settings";
import SettingsCategories from "./pages/settings/SettingsCategories";
import FAQSection from "./pages/FAQSection";
import GetStarted from "./pages/GetStarted";
import OnboardingPage from "./pages/OnboardingPage";
import AcknowledgeMagicLink from "./pages/AcknowledgeMagicLink";
import JoinPage from "./pages/JoinPage";
import StaffDashboard from "./pages/StaffDashboard";
import Composer from "./pages/Composer";
import ImportPolicies from "./pages/ImportPolicies";
import SettingsProfile from "./pages/settings/SettingsProfile";
import SettingsTeam from "./pages/settings/SettingsTeam";
import SettingsNotifications from "./pages/settings/SettingsNotifications";
import SettingsBilling from "./pages/settings/SettingsBilling";
import SettingsUserProfile from "./pages/settings/SettingsUserProfile";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <SuperAdminBanner />
          <SoftCloseBanner />
          <MobileBottomNav />
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/signup" element={<Navigate to="/auth" replace />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/no-access" element={<NoAccess />} />
            <Route
              path="/pending-acknowledgements"
              element={
                <ProtectedRoute skipAcknowledgementCheck>
                  <PendingAcknowledgements />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<LandingPage />} />
            <Route path="/faq" element={<FAQSection />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/acknowledge" element={<AcknowledgeMagicLink />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/onboarding/:step" element={<OnboardingPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Index />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff-dashboard"
              element={
                <ProtectedRoute>
                  <StaffDashboard />
                </ProtectedRoute>
              }
            />
            {/* Route path intentionally retains /composer (internal identifier).
                User-facing name is "Policy Builder" — see Composer_to_Policy_Builder_Beta positioning doc. */}
            <Route
              path="/composer"
              element={
                <ProtectedRoute>
                  <Composer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/policies"
              element={
                <ProtectedRoute>
                  <Policies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/policies/import"
              element={
                <ProtectedRoute>
                  <ImportPolicies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/policies/new"
              element={
                <ProtectedRoute>
                  <CreatePolicy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/policies/:id"
              element={
                <ProtectedRoute>
                  <PolicyDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-export"
              element={
                <ProtectedRoute>
                  <AuditExport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            >
              {/* Settings.tsx handles /settings → /settings/profile redirect internally */}
              <Route path="my-profile" element={<SettingsUserProfile />} />
              <Route path="profile" element={<SettingsProfile />} />
              <Route path="categories" element={<SettingsCategories />} />
              <Route path="team" element={<SettingsTeam />} />
              <Route path="notifications" element={<SettingsNotifications />} />
              <Route path="billing" element={<SettingsBilling />} />
            </Route>
            {/* Redirect old /team route */}
            <Route path="/team" element={<Navigate to="/settings/team" replace />} />
            <Route path="/superadmin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
            <Route path="/superadmin/clinic/:clinicId" element={<SuperAdminRoute><SuperAdminClinicDetail /></SuperAdminRoute>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
